import {
  AfterViewInit,
  booleanAttribute, ChangeDetectorRef,
  Component, computed, DestroyRef, effect, ElementRef, inject,
  input, Injector,
  OnDestroy, OnInit, output,
  Signal, signal,
  TemplateRef, untracked, viewChild, WritableSignal
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FocusMonitor } from '@angular/cdk/a11y';
import {
  AbstractControl, ControlEvent, ControlValueAccessor,
  FormControl,
  FormGroupDirective, FormResetEvent,
  NgControl,
  NgForm,
  ReactiveFormsModule,
  TouchedChangeEvent,
  ValidationErrors,
  ValidatorFn, Validators
} from '@angular/forms';
import { TranslateDirective, TranslateService } from '@ngx-translate/core';
import { MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MAT_FORM_FIELD, MatFormFieldControl } from '@angular/material/form-field';
import { MatAutocomplete, MatAutocompleteOrigin, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, retry, switchMap } from 'rxjs/operators';
import {
  AutoSuggestFilterBy,
  AutoSuggestSymbol,
  BackendError,
  MarketType,
  MarketTypeHelper,
  SecurityInfo,
  SecuritySymbol,
  SecuritySymbolHelper,
  SignalFormBridgeService,
  SymbolLookupDataSource,
  SymbolLookupItemLabelComponent,
  SymbolLookupService,
  SymbolType,
  TranslatePipe,
  ValueAccessorProviderFactory
} from '@tdwb/core';
@Component({
  selector: 'td-wb-symbol-lookup-material',
  templateUrl: 'symbol-lookup-material.component.html',
  styleUrls: ['symbol-lookup-material.component.scss'],
  providers: [
    ValueAccessorProviderFactory(SymbolLookupMaterialComponent),
    {
      provide: MatFormFieldControl,
      useExisting: SymbolLookupMaterialComponent
    }
  ],
  imports: [
    MatInput,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    SymbolLookupItemLabelComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    MatIcon,
    MatSuffix,
    MatIconButton,
    TranslateDirective,
    TranslatePipe
  ],
  host: {
    '[attr.id]': 'id',
    '[class.td-wb-symbol-lookup--hover]': 'hoverState()',
    '[class.td-wb-symbol-lookup--focus]': 'focused'
  }
})
export class SymbolLookupMaterialComponent<T extends AutoSuggestSymbol> implements ControlValueAccessor, MatFormFieldControl<T | string>, OnInit, AfterViewInit, OnDestroy {
  private static readonly MAX_RESULTS_DEFAULT: number = 7;
  private static readonly MAX_RESULTS_COMPACT: number = 5;
  static nextId: number = 0;
  readonly id: string = `td-wb-symbol-lookup-${SymbolLookupMaterialComponent.nextId++}`;
  readonly name = input<string>('td-wb-symbol-lookup-input');
  readonly showErrorBeforeSubmit = input<boolean>(true);
  readonly lenient = input<boolean, unknown>(false, { transform: booleanAttribute });
  readonly maxResults = input<number>(SymbolLookupMaterialComponent.MAX_RESULTS_DEFAULT);
  readonly maxResultsCompact = input<number>(SymbolLookupMaterialComponent.MAX_RESULTS_COMPACT);
  readonly market = input<MarketType, string>(undefined, { transform: (value: string): MarketType => MarketTypeHelper.fromString(value) });
  readonly symbolTypes = input<SymbolType | Array<SymbolType>>(undefined);
  readonly debounce = input<number>(300);
  readonly customDisplayFn = input<(value: T) => string>(undefined);
  readonly dataSource = input<SymbolLookupDataSource>(undefined);
  readonly placeholderKey = input<string>('tdWb.components.symbolLookup.PLACEHOLDER');
  readonly customTemplate = input<TemplateRef<unknown>>(undefined);
  readonly preSelectFirstItem = input<boolean, unknown>(true, { transform: booleanAttribute });
  readonly recentSymbolSearch = input<boolean, unknown>(false, { transform: booleanAttribute });
  readonly fractionalSymbolSearch = input<boolean, unknown>(false, { transform: booleanAttribute });
  readonly symbolLookupValidation = input<boolean, unknown>(false, { transform: booleanAttribute });
  readonly filterBy = input<AutoSuggestFilterBy>(undefined);
  readonly _required = input<boolean, unknown>(false, {
    alias: 'required',
    transform: booleanAttribute
  });

  readonly _disabledByInput = input<boolean, unknown>(false, {
    alias: 'disabled',
    transform: booleanAttribute
  });

  readonly onSelect = output<SecurityInfo>();
  readonly onInputChange = output<string>();
  readonly onHoverOrActive = output<{
    hoverState: boolean;
    focusState: boolean;
  }>();

  readonly checkSymbol: WritableSignal<boolean> = signal(false);
  readonly symbols: WritableSignal<Array<AutoSuggestSymbol>> = signal([]);
  readonly compactSymbolLabel: WritableSignal<boolean> = signal(false);
  readonly isRecentSearch: WritableSignal<boolean> = signal(false);
  symbolLookupFormControl: FormControl<T | string>;
  protected readonly formField: MatFormField | null = inject(MAT_FORM_FIELD, {
    optional: true
  });

  readonly stateChanges: Subject<void> = new Subject<void>();
  readonly symbolLookupInput: Signal<HTMLInputElement> = viewChild.required<HTMLInputElement>('symbolLookupInput');
  readonly symbolLookupAutocomplete: Signal<MatAutocomplete> = viewChild.required<MatAutocomplete>('auto');
  readonly focusOnInput: WritableSignal<boolean> = signal(false);
  readonly hoverState: WritableSignal<boolean> = signal(false);
  readonly touched: WritableSignal<boolean> = signal(false);
  readonly describedbyIds: WritableSignal<string | null> = signal(null);
  private readonly _focused: WritableSignal<boolean> = signal(false);
  /** Single source of truth. Written by `writeValue()` (no echo) and `setModel()` (user interaction, echoes). */
  private readonly _value: WritableSignal<T | string | null> = signal(null);
  private readonly _disabledByCva: WritableSignal<boolean> = signal(false);
  private readonly _disabled: Signal<boolean> = computed(() => this._disabledByInput() || this._disabledByCva());
  private readonly symbolInputValueChange: Subject<string> = new Subject<string>();
  private readonly _focusMonitor: FocusMonitor = inject(FocusMonitor);
  private readonly _elementRef: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly parentForm: NgForm | null = inject(NgForm, { optional: true });
  private readonly parentFormGroup: FormGroupDirective | null = inject(FormGroupDirective, { optional: true });
  private readonly defaultDataSource: SymbolLookupDataSource = inject(SymbolLookupService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly bridgeService: SignalFormBridgeService = inject(SignalFormBridgeService);
  private readonly injector: Injector = inject(Injector);
  private onChangeCallback: (value: T | string) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};
  private ngControlRef: NgControl | null = null;
  private isNgControlResolved: boolean = false;
  private isUsingFallbackControl: boolean = false;
  private currentMaxResult: number = SymbolLookupMaterialComponent.MAX_RESULTS_DEFAULT;
  get value(): T | string | null { return this._value(); }
  private set value(v: T | string) {
    if (v !== this._value()) {
      this._value.set(v);
      this.onChangeCallback(v);
    }
  }

  /** Required by `MatFormFieldControl`. Resolved lazily — see `resolveNgControl()`. */
  get ngControl(): NgControl | null { return this.resolveNgControl(); }
  get focused(): boolean { return this._focused(); }
  get placeholder(): string { return this.translateService.instant(this.placeholderKey()) }
  get required(): boolean { return this._required(); }
  get disabled(): boolean { return this._disabled(); }
  get errorState(): boolean {
    const parent = this.parentForm || this.parentFormGroup;
    return this.symbolLookupFormControl?.invalid &&
      ((this.touched() && this.showErrorBeforeSubmit()) || parent?.submitted);
  }

  get empty(): boolean {
    return !this.symbolLookupFormControl?.value;
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  /**
   * Origin used by the mat-autocomplete overlay so that the panel is
   * anchored to the mat-form-field's internal flex container (the row
   * containing the input) rather than the whole form-field host.
   *
   * Using the form-field host as the origin causes the overlay to be
   * positioned below the subscript wrapper (hints / mat-error), which
   * introduces a visible vertical gap that grows when an error is shown.
   * `MatFormField.getConnectedOverlayOrigin()` returns the ElementRef of
   * `.mat-mdc-form-field-flex`, which is what mat-select and the native
   * mat-autocomplete integration use for the exact same reason.
   */
  protected readonly autocompleteOrigin: Signal<MatAutocompleteOrigin | null> = computed(() => {
    const formField = this.formField;
    if (!formField) {
      return null;
    }
    return { elementRef: formField.getConnectedOverlayOrigin() };
  });

  constructor() {
    effect(() => {
      const required = this._required();
      const hasRequired = this.symbolLookupFormControl?.hasValidator(Validators.required);
      if (required && !hasRequired) {
        this.symbolLookupFormControl?.addValidators(Validators.required);
      } else if (!required && hasRequired) {
        this.symbolLookupFormControl?.removeValidators(Validators.required);
      }
      this.symbolLookupFormControl?.updateValueAndValidity();
    });
    effect(() => {
      const symbolLookupValidation = this.symbolLookupValidation();
      const hasSymbolLookupValidation = this.symbolLookupFormControl?.hasValidator(this.symbolLookupValidator());
      if (symbolLookupValidation && !hasSymbolLookupValidation) {
        this.symbolLookupFormControl?.addValidators(this.symbolLookupValidator());
      } else if (!symbolLookupValidation && hasSymbolLookupValidation) {
        this.symbolLookupFormControl?.removeValidators(this.symbolLookupValidator());
      }
      this.symbolLookupFormControl?.updateValueAndValidity();
    });
    effect(() => {
      this.placeholderKey();
      this._required();
      this._disabled();
      this._focused();
      untracked(() => this.stateChanges.next());
    });
    effect(() => {
      if (this._disabled()) {
        untracked(() => this.symbolLookupFormControl?.disable());
      } else {
        untracked(() => this.symbolLookupFormControl?.enable());
      }
    });
  }

  ngOnInit(): void {
    this.attachToOuterControl();
    this.setupSymbolInputValueChangeObserver();
  }

  ngAfterViewInit(): void {
    // Retry: under reactive forms (`formControlName`), `FormControlName.control` is
    // sometimes not yet linked when our own `ngOnInit()` runs, so `attachToOuterControl()`
    // falls back to a standalone control. By `ngAfterViewInit`, the whole initial view
    // (including all sibling form directives on this host element) has finished
    // initializing, so the real control should now be resolvable. See
    // `AccountSelectorMaterialComponent` for the same pattern (hazard H5).
    if (!this.isUsingFallbackControl) {
      return;
    }

    const mutableOuterControl = this.bridgeService.getMutableControl(this.resolveNgControl()) as FormControl<T | string> | null;
    if (mutableOuterControl === null) {
      return;
    }

    const pendingValue = this.symbolLookupFormControl.value;
    this.isUsingFallbackControl = false;
    this.symbolLookupFormControl = mutableOuterControl;
    this.wireSymbolLookupFormControl();
    if (pendingValue !== null && pendingValue !== undefined && this.symbolLookupFormControl.value == null) {
      // Must emit (the default), not suppress: same reasoning as `AccountSelectorMaterialComponent`
      // (hazard H5) - `wireSymbolLookupFormControl()` was just called above, so its
      // `valueChanges` subscription is already active and will run `writeValue()` for us.
      this.symbolLookupFormControl.setValue(pendingValue);
    }
    this.changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  setDescribedByIds(ids: Array<string>): void {
    this.describedbyIds.set(ids.join(' '));
  }

  onContainerClick(): void {
    if (!this.symbolLookupAutocomplete().isOpen) {
      this._focusMonitor.focusVia(this.symbolLookupInput(), 'program');
    }
  }

  onFocusIn(): void {
    if (!this._focused()) {
      this._focused.set(true);
    }
  }

  onFocusOut(event: FocusEvent): void {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      // This checks that the form control holds a string value and is not empty
      if (this.isNotEmptyStringValue()) {
        const symbolInput = typeof this.symbolLookupFormControl?.value === 'string' ?
          this.symbolLookupFormControl?.value : undefined;
        if (symbolInput && this.isValidSymbol(symbolInput)) {
          this.onSearchButtonClicked();
        }
      }
      this.touched.set(true);
      this._focused.set(false);
      this.onTouchedCallback();
    }
  }

  getStringInput(): string {
    if (typeof this.symbolLookupFormControl.value === 'string') {
      return this.symbolLookupFormControl.value;
    } else if (this.symbolLookupFormControl.value) {
      return this.symbolLookupFormControl.value.symbol + ' ' + this.symbolLookupFormControl.value.market;
    } else {
      return '';
    }
  }

  inputChange(inputValue: string): void {
    if (inputValue === undefined || inputValue === '') {
      this.resetCachedValues();
      this.onInputChange.emit(inputValue);
      this.updateValidity();
      this.symbolInputValueChange.next(''); // when input is empty trigger reset on searched items
    } else {
      const newValue = inputValue.toUpperCase();
      this.symbolLookupFormControl?.setValue(newValue, { emitEvent: false });
      this.querySymbols(newValue);
      if (this.checkSymbol() && !this.isValidSymbol(inputValue)) {
        this.checkSymbol.set(false);
      }
      this.onInputChange.emit(newValue);
      this.updateValidity();
    }
  }

  /**
   * Reset previously retrieved values.
   */
  resetCachedValues(): void {
    this.writeValue(null);
    this.symbolLookupFormControl?.reset();
    this.symbols.set([]);
    this.updateValidity();
  }

  updateValidity(): void {
    this.symbolLookupFormControl?.updateValueAndValidity();
    this.changeDetectorRef.markForCheck();
  }

  querySymbols(symbolQuery: string): void {
    if (symbolQuery === undefined) {
      this.symbols.set([]);
    } else {
      this.symbolInputValueChange.next(symbolQuery);
    }
  }

  displayFn(item: T | string): string {
    if (!item) {
      return '';
    }
    if (typeof item === 'string') {
      return item;
    }
    if (item.symbolType === SymbolType.MUTUALFUND) {
      return item.symbol ?? '';
    }
    return item.market !== undefined ? `${item.symbol} ${item.market}` : '';
  }

  optionSelected(symbol: SecuritySymbol | AutoSuggestSymbol): void {
    if (symbol) {
      this.setModel(symbol);
      this.checkSymbol.set(true);
      this.onSelect.emit(symbol as SecurityInfo);
      this.symbols.set([]);
      this.changeDetectorRef.markForCheck();
    }
  }

  writeValue(value: T | string): void {
    if (typeof value === 'string') {
      const currentValue = typeof this.symbolLookupFormControl?.value === 'string' ?
        this.symbolLookupFormControl?.value : this.symbolToString(this.symbolLookupFormControl?.value);
      if (currentValue !== value) {
        this.symbolLookupFormControl?.setValue(value, { emitEvent: false });
        this.inputChange(value);
      }
    } else {
      const inputValue = this.symbolToString(value);
      if (this.symbolLookupFormControl?.value !== value) {
        this.symbolLookupFormControl?.setValue(value, { emitEvent: false });
        if (value !== this._value()) {
          this._value.set(value);
        }
        if (inputValue && inputValue !== '' && this.isValidSymbol(inputValue)) {
          this.checkSymbol.set(true);
        }
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  registerOnChange(fn: (value: T | string) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
  }

  setRecentSearches(inputValue: string): void {
    this.focusOnInput.set(true);
    if (this.checkRecentSearchAllowed(inputValue)) {
      this.getRecentSearches().subscribe({
        next: (result: Array<T>): void => {
          if (result.length > 0) {
            this.symbols.set(result);
            this.isRecentSearch.set(true);
          }
        },
        error: (errorResponse: Array<BackendError>): void => {
          if (errorResponse) {
            this.symbols.set([]);
          }
        }
      });
    }
  }

  inputBlur(): void {
    this.focusOnInput.set(false);
  }

  displayLabelRecentSearches(): boolean {
    return this.isRecentSearch() && this.recentSymbolSearch();
  }

  checkRecentSearchAllowed(inputValue: string): boolean {
    return (inputValue === undefined || inputValue === null || inputValue === '') && this.recentSymbolSearch();
  }

  onSearchButtonClicked(): void {
    if (!this.disabled && this.isNotEmptyStringValue() && this.symbols().length > 0) {
      this.validateAndInvokeOnSelect();
    } else if (this.isNotEmptyStringValue() && this.symbols().length === 0) {
      this.symbolLookupFormControl?.markAsTouched();
      this.touched.set(true);
      this.onTouchedCallback();
    }
    this.updateValidity();
  }

  private setModel(symbol: SecuritySymbol | AutoSuggestSymbol, overwrite: boolean = true): void {
    if (typeof this.value !== 'string') {
      if (overwrite || (this.value === undefined || this.value === null ||
        (symbol !== undefined && this.isSecuritySymbol(this.value) &&
          (symbol.symbol !== this.value.symbol || symbol.marketType !== this.value.marketType)))) {
        this.value = symbol as T;
      }
    }
  }

  private isSecuritySymbol(symbol: SecuritySymbol | AutoSuggestSymbol): symbol is SecuritySymbol {
    return (symbol as SecuritySymbol).symbol !== undefined;
  }

  private isValidSymbol(symbolString: string): boolean {
    let symbol: SecuritySymbol;
    const isValid = SecuritySymbolHelper.isValid(symbolString, this.symbolTypes());
    if (isValid && this.market() !== undefined) {
      symbol = SecuritySymbolHelper.parse(symbolString);
      if (this.market() !== symbol.market) {
        return false;
      }
    }
    return isValid;
  }

  private setupSymbolInputValueChangeObserver(): void {
    this.symbolInputValueChange
      .pipe(
        debounceTime(this.debounce()),
        switchMap((symbolQuery: string): Observable<Array<AutoSuggestSymbol>> => {
          // prevents issue when holding backspace was receiving an older, delayd string value instead of empty string
          if (symbolQuery === '') {
            // below condition added to preserve recent search symbols when focus on the symbol lookup input box
            if (this.isRecentSearch()) {
              return of(this.symbols());
            }
            return of([]);
          }
          return this.doSearch(symbolQuery).pipe(
            // if the search fails, retry the request 2 times
            retry(2),
            // if it still fails, catch the error to prevent the main stream from dying
            catchError(() => {
              // return an empty array to clear the results and allow for future searches
              return of([]);
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (result: Array<AutoSuggestSymbol>): void => {
          this.symbols.set(result);
          // below condition added to preserve recent search symbols when focus on the symbol lookup input box
          if (this.symbolLookupFormControl.value !== '') {
            this.isRecentSearch.set(false);
          }
          // Commented out since goalassist clients are able to sell a non-TD ETF that they hold on their portfolio
          // As a direct consequence, on trading, when picking a symbol then later pasting a well-formed but nonexistent symbol,
          //   it will not clear symbol model, not fire invalid error, and quote component wont refresh,
          //   so hitting send will trade old symbol
          // if (result.length === 0 && this.checkSymbol) {
          //   this.checkSymbol = false;
          //   this.setModel(undefined);
          // }
          this.changeDetectorRef.markForCheck();
        }
        // no error handler is needed here, as it's handled in the stream now
      });
  }

  private doSearch(symbolQuery: string): Observable<Array<AutoSuggestSymbol>> {
    if (this.dataSource() !== undefined) {
      return this.dataSource().search<AutoSuggestSymbol>(symbolQuery,
        this.currentMaxResult,
        this.symbolTypes(),
        this.market(),
        this.fractionalSymbolSearch(),
        this.filterBy()
      );
    }
    return this.defaultDataSource.search<AutoSuggestSymbol>(
      symbolQuery,
      this.currentMaxResult,
      this.symbolTypes(),
      this.market(),
      this.fractionalSymbolSearch(),
      this.filterBy()
    );
  }

  private symbolToString(value: T): string {
    if (value === undefined || value === null) {
      return '';
    }
    return SymbolLookupService.symbolToString(value);
  }

  private validateAndInvokeOnSelect(): void {
    let newSymbol: SecuritySymbol | AutoSuggestSymbol;
    const symbolInput = typeof this.symbolLookupFormControl?.value === 'string' ?
      this.symbolLookupFormControl?.value : undefined;
    if (this.isNotEmptyStringValue()) {
      if (this.isValidSymbol(symbolInput)) {
        // when user has a well-formed string and focus out/click search (instead of picking from type-ahead)
        // we will first try picking from first item on the last autosuggest list received,
        if (this.symbols() !== undefined && this.symbols().length > 0) {
          newSymbol = this.symbols()[0];
        } else { // otherwise fallback to parsing string - which will contain minimal info {symbol, market & marketType}
          newSymbol = SecuritySymbolHelper.parse(symbolInput);
        }
      }
    }
    this.optionSelected(newSymbol);
  }

  private isNotEmptyStringValue(): boolean {
    return this.symbolLookupFormControl?.value && typeof this.symbolLookupFormControl?.value === 'string' &&
      this.symbolLookupFormControl?.value !== '';
  }

  private getRecentSearches(): Observable<Array<T>> {
    const dataSource = this.dataSource();
    if (dataSource !== undefined) {
      return dataSource.getRecentSymbols(this.currentMaxResult, this.symbolTypes(), this.market());
    }
    return this.defaultDataSource.getRecentSymbols(this.currentMaxResult, this.symbolTypes(), this.market());
  }

  private symbolLookupValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const enteredSymbol = control?.value;
      if(enteredSymbol && typeof enteredSymbol === 'object' && this.market() !== undefined) {
        return enteredSymbol.market === this.market() ? null : { invalid: true };
      } else if(typeof enteredSymbol === 'string' && enteredSymbol !== '') {
        // Need to consider lenient here because the form control value can be a string now
        return this.lenient() ? null : { invalid: true };
      }
      return null;
    };
  }

  /**
   * Resolved lazily: injecting `NgControl` eagerly while this component is also registered
   * as its own `NG_VALUE_ACCESSOR` creates an NG0200 circular dependency under `[formField]`.
   */
  private resolveNgControl(): NgControl | null {
    if (!this.isNgControlResolved) {
      this.ngControlRef = this.bridgeService.getNgControl(this.injector);
      this.isNgControlResolved = true;
    }

    return this.ngControlRef;
  }

  /**
   * Reuse the owning control when it is a real, mutable `AbstractControl` (classic Angular
   * Forms), so `errorState`/touched/validity naturally reflect the parent form. Falls back to
   * a standalone control when no real one is available yet — retried from `ngAfterViewInit()`.
   */
  private attachToOuterControl(): void {
    const mutableOuterControl = this.bridgeService.getMutableControl(this.resolveNgControl()) as FormControl<T | string> | null;
    this.isUsingFallbackControl = mutableOuterControl === null;
    this.symbolLookupFormControl = mutableOuterControl ?? new FormControl<T | string>(undefined);
    this.wireSymbolLookupFormControl();
  }

  private wireSymbolLookupFormControl(): void {
    this.symbolLookupFormControl.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {
      this.stateChanges.next();
    });
    this.symbolLookupFormControl?.valueChanges?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {
      this.writeValue(this.symbolLookupFormControl.value ?? null);
    });
    this.symbolLookupFormControl.events.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: ControlEvent): void => {
        if (event instanceof FormResetEvent) {
          this.touched.set(false);
          this.stateChanges.next();
        } else if (event instanceof TouchedChangeEvent) {
          // The parent form's submit() marks invalid controls as touched. That change is not
          // observable through statusChanges (validity did not change), so sync our internal
          // touched signal here and notify the mat-form-field to re-evaluate errorState.
          if (this.touched() !== event.touched) {
            this.touched.set(event.touched);
            this.stateChanges.next();
          }
        }
      });
  }
}
